// ABOUTME: One onboarding chapter in the overview column: icon, title and subtitle, status label,
// ABOUTME: step counter with percentage, a segmented bar per step, and the next-step action.

import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import CallReceivedOutlinedIcon from "@mui/icons-material/CallReceivedOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import EuroOutlinedIcon from "@mui/icons-material/EuroOutlined";
import IntegrationInstructionsOutlinedIcon from "@mui/icons-material/IntegrationInstructionsOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import { Box, ButtonBase, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import Shbutton from "../../../../../components/theme/buttons/Shbutton";
import ShLabel from "../../../../../components/theme/label/ShLabel";
import Text from "../../../../../components/theme/text/Text";
import { tokens } from "../../../../../theme";

// The card surface the totals panels use above a tab view (see ShTotalsPanel).
export const panelSx = (theme, colors) => ({
    background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
    boxShadow: "0px 1px 2px 1px rgba(49, 65, 95, 0.15)",
    borderRadius: 4,
});

export const CHAPTER_STATUS_LABEL = {
    finished: { key: "onboarding_finished", variant: "green" },
    busy: { key: "onboarding_busy", variant: "blue" },
    open: { key: "onboarding_not_started", variant: "orange" },
};

const CHAPTER_ICONS = {
    warehouse: WarehouseOutlinedIcon,
    office: PrintOutlinedIcon,
    shipping: LocalShippingOutlinedIcon,
    picking: AccountTreeOutlinedIcon,
    inbound: CallReceivedOutlinedIcon,
    orders: ShoppingCartOutlinedIcon,
    financial: EuroOutlinedIcon,
    return_portal: AssignmentReturnOutlinedIcon,
    integrations: IntegrationInstructionsOutlinedIcon,
    relation: ContactsOutlinedIcon,
};

// The square icon tile that heads a chapter card and the detail pane. Its colour carries the
// chapter status: green when finished, blue while busy, orange when not started.
export const ChapterIcon = ({ chapterKey, status, size = 40 }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const Icon = CHAPTER_ICONS[chapterKey] ?? WarehouseOutlinedIcon;

    let tint = theme.palette.mode === "dark" ? colors.blue : colors.primary;
    if (status === "finished") tint = colors.green;
    if (status === "open") tint = colors.orange;

    return (
        <Box
            sx={{
                width: size,
                height: size,
                borderRadius: 2,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: tint[100],
                color: tint[400],
            }}
        >
            <Icon fontSize="small" />
        </Box>
    );
};

// A finished chapter shows green, a chapter in progress shows the handled steps in blue.
export const stepColor = (colors, theme, step, chapterStatus) => {
    if (step.status === "skipped") return colors.orange[400];
    if (step.status === "done") {
        if (chapterStatus === "finished") return colors.green[400];
        return theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400];
    }
    return theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200];
};

// The step counter of a chapter: the step the user is on, capped at the last one.
export const currentStepNumber = (chapter) => Math.min(chapter.doneCount + 1, chapter.total);

const ChapterCard = ({ chapter, selected, onSelect }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const label = CHAPTER_STATUS_LABEL[chapter.status] ?? CHAPTER_STATUS_LABEL.open;
    const nextStep = chapter.steps.find((step) => step.status === "open");
    const percent = chapter.total > 0 ? Math.round((chapter.doneCount / chapter.total) * 100) : 0;
    const isFinished = chapter.status === "finished";

    const accent = theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400];
    const divider = `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`;

    let actionKey = "onboarding_continue";
    if (isFinished) actionKey = "onboarding_view";
    else if (chapter.status === "open") actionKey = "onboarding_start";

    return (
        <ButtonBase
            component="div"
            onClick={() => onSelect(chapter.key)}
            sx={{
                ...panelSx(theme, colors),
                width: 1,
                textAlign: "left",
                display: "block",
                outline: selected ? `2px solid ${accent}` : "none",
                padding: 2.5,
                transition: "outline 150ms",
                cursor: "pointer",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <ChapterIcon chapterKey={chapter.key} status={chapter.status} />
                    <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
                        <Text bold>{t(`onboarding_chapter_${chapter.key}`)}</Text>
                        <Text light>{t(`onboarding_chapter_${chapter.key}_subtitle`)}</Text>
                    </Box>
                </Box>
                <ShLabel size={32} fitted variant={label.variant}>{t(label.key)}</ShLabel>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Text semibold>
                    {isFinished
                        ? t("onboarding_steps_completed", { done: chapter.doneCount, total: chapter.total })
                        : t("onboarding_step_of", { current: currentStepNumber(chapter), total: chapter.total })}
                </Text>
                <Text light>{`${percent}%`}</Text>
            </Box>

            <Box sx={{ display: "flex", gap: 0.75, mb: 2 }}>
                {chapter.steps.map((step) => (
                    <Box
                        key={step.key}
                        title={t(`onboarding_step_${chapter.key}_${step.key}`)}
                        sx={{
                            flex: 1,
                            height: 6,
                            borderRadius: 3,
                            background: stepColor(colors, theme, step, chapter.status),
                        }}
                    />
                ))}
            </Box>

            <Box sx={{ borderTop: divider, paddingTop: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
                    <Text light>{isFinished ? t("onboarding_all_configured") : t("onboarding_next_step")}</Text>
                    <Text bold>
                        {nextStep ? t(`onboarding_step_${chapter.key}_${nextStep.key}`) : t("onboarding_chapter_finished")}
                    </Text>
                </Box>
                <Shbutton
                    variant="contained"
                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                    endIcon={isFinished ? <VisibilityOutlinedIcon /> : <ArrowForwardIcon />}
                    onClick={(event) => {
                        event.stopPropagation();
                        onSelect(chapter.key);
                    }}
                >
                    {t(actionKey)}
                </Shbutton>
            </Box>
        </ButtonBase>
    );
};

export default ChapterCard;
